"use client";

import { useEffect, useRef, useState } from "react";

const DEFAULT_CHARS =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789/<>-_=()";

function buildRevealOrder(text: string) {
    const revealOrder = text
        .split("")
        .map((char, index) => ({ char, index }))
        .filter(({ char }) => char !== " " && char !== "\t" && char !== "\n")
        .map(({ index }) => index);

    for (let i = revealOrder.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [revealOrder[i], revealOrder[j]] = [revealOrder[j], revealOrder[i]];
    }

    return revealOrder;
}

function scramble(
    text: string,
    revealCount: number,
    revealOrder: number[],
    chars: string,
) {
    const revealSet = new Set(revealOrder.slice(0, revealCount));
    return text
        .split("")
        .map((char, index) => {
            if (char === " " || char === "\t" || char === "\n") {
                return char;
            }
            if (revealSet.has(index)) {
                return char;
            }
            return chars[Math.floor(Math.random() * chars.length)];
        })
        .join("");
}

type ScrambledTextProps = {
    text: string;
    className?: string;
    durationMs?: number;
    scrambleChars?: string;
};

export default function ScrambledText({
    text,
    className,
    durationMs,
    scrambleChars = DEFAULT_CHARS,
}: ScrambledTextProps) {
    const rafRef = useRef<number | null>(null);
    const revealOrderRef = useRef<number[]>([]);
    const [display, setDisplay] = useState(text);

    useEffect(() => {
        revealOrderRef.current = buildRevealOrder(text);

        const resolvedDuration =
            durationMs ?? Math.min(1800, Math.max(600, text.length * 24));
        const startTime = performance.now();

        const tick = (now: number) => {
            const progress = Math.min(1, (now - startTime) / resolvedDuration);
            const revealCount = Math.floor(
                progress * revealOrderRef.current.length,
            );
            setDisplay(
                scramble(text, revealCount, revealOrderRef.current, scrambleChars),
            );

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setDisplay(text);
            }
        };

        setDisplay(scramble(text, 0, revealOrderRef.current, scrambleChars));
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [text, durationMs, scrambleChars]);

    return <span className={className}>{display}</span>;
}
